import { useTranslation } from "react-i18next";
import { PlayerScheduleConf } from "./PlayerScheduleDetailUtils";
import { MonitorIcon, PlaySquareIcon } from "lucide-react";
import { format } from "date-fns";
import { SelectedMediaPreview } from "@/pages/shared/SelectedMediaPreview";

interface ScheduleSectionProps {
  conf: PlayerScheduleConf;
  type: "channel" | "campaign";
  isOverridenByArea?: boolean;
}

export const ScheduleSection = ({ conf, type, isOverridenByArea }: ScheduleSectionProps) => {
  const { t } = useTranslation();
  const now = new Date();

  const getScheduleStatus = (start: Date, end: Date): "upcoming" | "current" | "past" | "out-of-date" | "overriden" => {
    if (isOverridenByArea) {
      return "overriden";
    }

    if (now < start) return "upcoming";
    if (now > end) return "past";
    return "current";
  };

  return (
    <div className="mb-2">
      <div className="flex items-center gap-2 py-2 px-4 bg-muted/50 rounded-md">
        {type === "channel" ? (
          <MonitorIcon className="h-4 w-4 text-primary" />
        ) : (
          <PlaySquareIcon className="h-4 w-4 text-primary" />
        )}
        <h3 className="text-sm font-medium">{conf.name}</h3>
      </div>

      {conf.items.map((item, idx) => {
        const status = getScheduleStatus(item.periodStart, item.periodEnd);
        const statusColors = {
          upcoming: "border-blue-400 bg-blue-50 dark:bg-blue-950",
          current: "border-green-400 bg-green-50 dark:bg-green-950",
          past: "border-gray-300 bg-gray-50 dark:bg-gray-900 opacity-75",
          overriden: "border-red-400 bg-red-50 dark:bg-red-950",
          "out-of-date": "border-gray-300 bg-gray-50 dark:bg-gray-900 opacity-75",
        };

        return (
          <div
            key={idx}
            className={`pl-4 border-l-2 ml-6 mt-2 p-2 rounded-r-md transition-colors ${statusColors[status]}`}
          >
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <span className={status === "overriden" ? "line-through" : ""}>
                {format(new Date(item.periodStart), "MMM dd")} - {format(new Date(item.periodEnd), "MMM dd, yyyy")}
              </span>
              <span
                className={`
                  px-2 py-0.5 rounded-full text-xs font-medium
                  ${status === "upcoming" && "bg-blue-200 text-blue-700 dark:bg-blue-900 dark:text-blue-300"}
                  ${status === "current" && "bg-green-200 text-green-700 dark:bg-green-900 dark:text-green-300"}
                  ${status === "past" && "bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-400"}
                  ${status === "overriden" && "bg-red-200 text-red-700 dark:bg-red-900 dark:text-red-300"}
                  ${status === "out-of-date" && "bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-400"}
                `}
              >
                {t(`schedule.status.${status}`)}
              </span>
            </div>
            <div className="gap-x-4 gap-y-1">
              {item.dates.map((schedule, scheduleIdx) => (
                <div>
                  <div className="flex items-center gap-2 p-1" key={scheduleIdx}>
                    <div className={`text-sm font-medium w-20 ${status === "overriden" ? "line-through" : ""}`}>
                      {t(`channel.days.${schedule.dayOfWeek}`)}
                    </div>
                    <div className="space-y-1">
                      <div className={`text-sm text-muted-foreground ${status === "overriden" ? "line-through" : ""}`}>
                        {schedule.startTime} - {schedule.endTime}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div className={`pl-2 border-l $  {statusColors[status]}`}>
                {item.contents?.length > 0 && (
                  <div className="pl-2">
                    {item.contents?.map((content, contentIdx) => (
                      <SelectedMediaPreview
                        key={contentIdx}
                        contentIntId={content.contentintId}
                        contentGroupIntId={content.contentGroupintId}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
